import { graphql } from 'gatsby';
import { useCustomer } from 'gatsby-theme-hem-store/src/hooks/useCustomer';
import first from 'lodash/first';
import isString from 'lodash/isString';
import { Paperclip } from 'lucide-react';
import React from 'react';

import { SectionTitle } from '@/components/SectionTitle';

const Downloads: React.FC<{
  data: Queries.DownloadsSliceQuery;
  showGradedInDownloadOption?: boolean;
  type: string;
}> = ({ data, showGradedInDownloadOption = false, type = '' }) => {
  const { customer } = useCustomer();

  const downloads = [
    {
      key: 'Assembly instructions',
      value: first(data.downloads.assemblyInstructions),
    },
    {
      key: 'Care instructions',
      value:
        first(data.downloads.careInstructions) ||
        (type.toLowerCase() === 'part'
          ? null
          : 'https://go.hem.com/care-instructions/general'),
    },
    {
      key: 'Ecology sheet',
      value: first(data.downloads.ecologySheets),
    },
    {
      key: 'Fact sheet',
      value: first(data.downloads.factSheets),
    },
    {
      key: 'User guide',
      value: first(data.downloads.userGuides),
    },
    {
      key: '2D files',
      value: first(data.downloads._2DFiles),
    },
    {
      key: '3D files',
      value: first(data.downloads._3DFiles),
    },
  ].filter((o) => isString(o.value));

  if (showGradedInDownloadOption === true) {
    downloads.push({
      key: 'Graded-in textiles',
      value:
        customer !== undefined
          ? 'https://go.hem.com/made-to-order/pre-order-graded-in-textiles'
          : 'https://go.hem.com/made-to-order/graded-in-textiles',
    });
  }

  return downloads.length > 0 ? (
    <section className="mx-4 mb-16 md:mx-8">
      <SectionTitle title="Downloads" titleTag="h3" border={false} />
      <ul className="-mx-4 divide-y divide-black-100 border-y border-black-100 md:-mx-8">
        {downloads.map(({ key, value }) => {
          return (
            <li key={value}>
              <a
                href={value}
                className="group grid grid-cols-[auto,auto,1fr,auto] items-center gap-x-2 px-4 py-4 hover:bg-black-50 hover:no-underline md:px-8"
                data-selector={`PDP ${key} download button`}
                download
              >
                <Paperclip size={20} className="text-black-400" />
                <span className="text-base font-bold text-black">{key}</span>
                <span className="text-base text-black-400">
                  {key === '3D files' || key === '2D files' ? 'ZIP' : 'PDF'}
                </span>
                <span className="text-base font-bold text-blue group-hover:text-black">
                  Download
                </span>
              </a>
            </li>
          );
        })}
      </ul>
    </section>
  ) : null;
};

export default Downloads;

export const query = graphql`
  query DownloadsSlice($id: String) {
    downloads: airtableProduct(id: { eq: $id }) {
      assemblyInstructions
      careInstructions
      factSheets
      _3DFiles
      _2DFiles
      ecologySheets
      userGuides
    }
  }
`;
